// Presentation metadata for the marketplace, keyed by app id. The BFF /api/apps endpoint is the
// source of truth for *which* apps exist and the user's entitlement; this table only enriches the
// cards/detail with copy the registry doesn't carry (description, screenshots, price, screens).
export interface AppMeta {
  emoji: string;
  tint: string;
  category: string;
  publisher: string;
  summary: string;
  description: string;
  priceLabel?: string;
  screens: { id: string; label: string }[];
}

const DEFAULT: AppMeta = {
  emoji: "📦",
  tint: "#EEF1F4",
  category: "앱",
  publisher: "Special Partners · 내부 앱",
  summary: "compath add-on 앱",
  description: "compath 셸에서 실행되는 add-on sibling 앱입니다.",
  screens: [],
};

const CATALOG: Record<string, AppMeta> = {
  orders: {
    emoji: "🛒",
    tint: "#E8F1FE",
    category: "업무 · 주문",
    publisher: "Special Partners · 내부 앱",
    summary: "주문 생성·편집",
    description:
      "주문을 생성하고 편집하는 파일럿 sibling 앱입니다. 모든 화면은 orders sibling이 내려주는 " +
      "SDUI 문서를 compath 렌더러가 그대로 그립니다 — 화면 추가에 compath 코드 변경은 0줄.",
    screens: [{ id: "orders/edit", label: "주문 편집" }],
  },
  analytics: {
    emoji: "📊",
    tint: "#EEF1F4",
    category: "대시보드 · BI",
    publisher: "Special Partners · 내부 앱",
    summary: "매출·지표 대시보드",
    priceLabel: "₩49,000 / 월",
    description:
      "매출·주문·정산 데이터를 한 곳에서 보는 사내 분석 대시보드입니다. 일·주·월 지표, 코호트, " +
      "실시간 매출 추이를 제공하며 모든 화면은 compath SDUI로 렌더링됩니다.",
    screens: [
      { id: "analytics/home", label: "지표 요약 홈" },
      { id: "analytics/report", label: "기간별 매출 리포트" },
      { id: "analytics/cohort", label: "코호트 리텐션" },
    ],
  },
  settlement: {
    emoji: "💳",
    tint: "#E5F7EF",
    category: "업무 · 정산",
    publisher: "Special Partners · 내부 앱",
    summary: "정산 내역 조회",
    priceLabel: "₩29,000 / 월",
    description: "일·월 정산 내역과 지급 상태를 조회하는 앱입니다.",
    screens: [{ id: "settlement/home", label: "정산 홈" }],
  },
  inventory: {
    emoji: "📦",
    tint: "#EEF1F4",
    category: "업무 · 재고",
    publisher: "Special Partners · 내부 앱",
    summary: "실시간 재고 관리",
    priceLabel: "₩39,000 / 월",
    description: "실시간 재고 현황과 입출고를 관리하는 앱입니다.",
    screens: [{ id: "inventory/home", label: "재고 현황" }],
  },
  "beyond-cdx": {
    emoji: "🧊",
    tint: "#EEF2FF",
    category: "업무 · sibling",
    publisher: "Special Partners · 내부 앱",
    summary: "BeyondCDX",
    description:
      "compath sibling 헤드리스 백엔드(Go)입니다. 모든 화면은 beyond-cdx sibling이 내려주는 " +
      "SDUI 문서를 compath 렌더러가 그대로 그립니다 — 화면 추가에 compath 코드 변경은 0줄. " +
      "scripts/create-sibling.sh 로 repo·배포까지 세워진 첫 sibling입니다.",
    screens: [{ id: "beyond-cdx/edit", label: "편집" }],
  },
  "vuln-manager": {
    emoji: "🛡️",
    tint: "#FEF2F2",
    category: "보안 · 취약점 관리",
    publisher: "Special Partners · 내부 앱",
    summary: "VulnGuard — 취약점 관리",
    description:
      "스캔 결과(Trivy·Grype·SARIF·JSON·CSV)를 업로드해 취약점을 관리하는 sibling입니다. " +
      "CISA KEV·EPSS·NVD로 악용가능성과 CVSS를 도출하고, FDA 가이던스(악용가능성×심각도)로 위험을 " +
      "산정한 뒤 NIST SP 800-40 대응(remediate/mitigate/accept)을 관리하고 CycloneDX VEX로 내보냅니다.",
    screens: [{ id: "vuln-manager/board", label: "보드" }],
  },
  // ---- Scaffolded siblings ------------------------------------------------------------------
  // repo · CI · 배포 · 라우팅까지 끝났지만 기능 정의는 아직입니다. 카드 문구가 "준비 중"인 이유는
  // 정해지지 않은 기능을 있는 것처럼 적지 않기 위해서입니다 — 스코프가 정해지면 여기만 고치면 됩니다.
  "label-drawer": {
    emoji: "🏷️",
    tint: "#F5F3FF",
    category: "준비 중",
    publisher: "Special Partners · 내부 앱",
    summary: "Label Drawer — 준비 중",
    description:
      "제품 라벨·표시자료를 다루는 앱으로 자리를 잡아둔 sibling입니다. 저장소·CI·배포·라우팅은 이미 " +
      "동작하며, 지금은 연결 상태를 확인하는 홈 화면 하나만 제공합니다. 기능 정의가 끝나면 sibling " +
      "쪽 화면만 채우면 됩니다 (compath 코드 변경 0줄).",
    screens: [{ id: "label-drawer/home", label: "홈" }],
  },
  splite: {
    emoji: "⚡",
    tint: "#ECFEFF",
    category: "준비 중",
    publisher: "Special Partners · 내부 앱",
    summary: "SpLite — 준비 중",
    description:
      "SpLite 자리를 잡아둔 sibling입니다. 저장소·CI·배포·라우팅은 이미 동작하며, 지금은 연결 상태를 " +
      "확인하는 홈 화면 하나만 제공합니다. 기능 정의가 끝나면 sibling 쪽 화면만 채우면 됩니다.",
    screens: [{ id: "splite/home", label: "홈" }],
  },
  "temple-stay": {
    emoji: "🛕",
    tint: "#FFF7ED",
    category: "준비 중",
    publisher: "Special Partners · 내부 앱",
    summary: "Temple Stay — 준비 중",
    description:
      "템플스테이 관련 앱으로 자리를 잡아둔 sibling입니다. 저장소·CI·배포·라우팅은 이미 동작하며, " +
      "지금은 연결 상태를 확인하는 홈 화면 하나만 제공합니다. 기능 정의가 끝나면 sibling 쪽 화면만 " +
      "채우면 됩니다.",
    screens: [{ id: "temple-stay/home", label: "홈" }],
  },
  "with-claude": {
    emoji: "🤖",
    tint: "#F0F9FF",
    category: "준비 중",
    publisher: "Special Partners · 내부 앱",
    summary: "With Claude — 준비 중",
    description:
      "Claude 기반 업무 보조 앱으로 자리를 잡아둔 sibling입니다. 저장소·CI·배포·라우팅은 이미 " +
      "동작하며, 지금은 연결 상태를 확인하는 홈 화면 하나만 제공합니다. 기능 정의가 끝나면 sibling " +
      "쪽 화면만 채우면 됩니다.",
    screens: [{ id: "with-claude/home", label: "홈" }],
  },
};

export function metaFor(id: string): AppMeta {
  return CATALOG[id] ?? { ...DEFAULT, summary: id };
}
